import React, { useState } from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, Button, Stack, Avatar, Tooltip, Badge, useTheme, useMediaQuery } from '@mui/material';
import AppBackground from './AppBackground';
import {
  Dashboard as DashboardIcon,
  People as ClientIcon,
  ElectricBolt as UtilityIcon,
  RequestQuote as QuoteIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Business as SuppliersIcon,
  Notifications as NotificationsIcon,
  Campaign as MarketingIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import UserProfileDialog from '../User/UserProfileDialog';
import NotificationCenter from '../notifications/NotificationCenter';
import UserProfileDrawer from '../profile/UserProfileDrawer';
import MobileNavigation from '../MobileNavigation';

const UtilitiesLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { unreadCount } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);

  const handleNavigation = (path: string) => {
    if (path.startsWith('/utilities/admin')) {
      if (user?.role === 'administrator') {
        navigate(path);
      } else {
        navigate('/utilities/dashboard');
      }
    } else {
      navigate(path);
    }
  };

  // Full menu for desktop
  const desktopMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/utilities/dashboard' },
    { text: 'Contacts', icon: <ClientIcon />, path: '/utilities/leads' },
    { text: 'Clients', icon: <ClientIcon />, path: '/utilities/clients' },
    { text: 'Quote Tool', icon: <QuoteIcon />, path: '/utilities/quotes' },
    { text: 'Contracts', icon: <UtilityIcon />, path: '/utilities/contracts' },
    { text: 'Suppliers', icon: <SuppliersIcon />, path: '/utilities/suppliers' },
    { text: 'Marketing', icon: <MarketingIcon />, path: '/utilities/marketing/subscribers' },
    { text: 'Company Checker', icon: <SearchIcon />, path: '/utilities/company-checker' },
    ...(user?.role === 'administrator' ? [
      { text: 'Admin', icon: <SettingsIcon />, path: '/utilities/admin/lookups' },
    ] : [])
  ];

  // Streamlined menu for mobile
  const mobileMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/utilities/dashboard' },
    { text: 'Clients', icon: <ClientIcon />, path: '/utilities/clients' },
    { text: 'Quote Tool', icon: <QuoteIcon />, path: '/utilities/quotes' },
    { text: 'Contracts', icon: <UtilityIcon />, path: '/utilities/contracts' },
    { text: 'Marketing', icon: <MarketingIcon />, path: '/utilities/marketing/subscribers' },
    ...(user?.role === 'administrator' ? [
      { text: 'Admin', icon: <SettingsIcon />, path: '/utilities/admin/lookups' },
    ] : [])
  ];

  const menuItems = isMobile ? mobileMenuItems : desktopMenuItems;


  // Render mobile navigation if on mobile
  if (isMobile) {
    return (
      <AppBackground>
        <MobileNavigation user={user ? { name: `${user.firstName} ${user.lastName}`, email: user.email || '' } : undefined} />
        <Box sx={{ pt: 6, px: 1, pb: 10 }}>
          <Outlet />
        </Box>
        {user && (
          <>
            <NotificationCenter 
              open={notificationDrawerOpen} 
              onClose={() => setNotificationDrawerOpen(false)} 
            />
            <UserProfileDrawer
              open={profileDrawerOpen}
              onClose={() => setProfileDrawerOpen(false)}
              onOpenProfileDialog={() => setIsProfileDialogOpen(true)}
            />
            <UserProfileDialog
              open={isProfileDialogOpen}
              onClose={() => setIsProfileDialogOpen(false)}
              user={user}
            />
          </>
        )}
      </AppBackground>
    );
  }

  // Desktop layout
  return (
    <AppBackground>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            TPI Compliance
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="inherit">
              {user?.firstName} {user?.lastName}
            </Typography>
            
            {/* Notification Icon */}
            <Tooltip title="Notifications">
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setNotificationDrawerOpen(true)}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            
            {/* User Profile Avatar */}
            <Tooltip title="Profile">
              <IconButton color="inherit" onClick={() => {
            setProfileDrawerOpen(!profileDrawerOpen);
          }}>
              <Avatar 
                sx={{ width: 32, height: 32, bgcolor: 'primary.dark', cursor: 'pointer' }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
              </IconButton>
            </Tooltip>
            
          </Stack>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: 8, px: 3 }}>
        <Box sx={{ py: 2 }}>
          <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              width: '100%',
              justifyContent: 'space-evenly',
              mx: 'auto',
              mb: 3
            }}>
            {menuItems.map((item) => (
              <Button
                key={item.text}
                variant={location.pathname.startsWith(item.path) ? 'contained' : 'outlined'}
                startIcon={item.icon}
                onClick={() => handleNavigation(item.path)}
                size="large"
                sx={{
                  backdropFilter: 'blur(8px)',
                  borderColor: location.pathname.startsWith(item.path) ? 'transparent' : 'rgba(255, 255, 255, 0.2)',
                  backgroundColor: location.pathname.startsWith(item.path) ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: location.pathname.startsWith(item.path) ? '#2563eb' : 'rgba(255, 255, 255, 0.15)',
                    borderColor: location.pathname.startsWith(item.path) ? 'transparent' : 'rgba(255, 255, 255, 0.3)',
                  },
                  padding: '10px 16px',
                  fontSize: '0.95rem',
                  fontWeight: 'medium',
                  minWidth: '120px',
                  flex: '1 1 auto',
                  maxWidth: '180px',
                  height: '48px',
                  textTransform: 'none',
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>
        </Box>

        <Box 
          sx={{ 
            py: 3,
            px: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
            mt: 2,
          }}
        >
          <Outlet />
        </Box>
      </Box>
      {user && (
        <>
          <NotificationCenter 
            open={notificationDrawerOpen} 
            onClose={() => setNotificationDrawerOpen(false)} 
          />
          
          {/* User Profile Drawer */}
          <UserProfileDrawer
        open={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
        onOpenProfileDialog={() => setIsProfileDialogOpen(true)}
      />
      
      <UserProfileDialog
          open={isProfileDialogOpen}
          onClose={() => setIsProfileDialogOpen(false)}
          user={user}
        />
        </>
      )}
    </Box>
    </AppBackground>
  );
};

export default UtilitiesLayout;
